import { useState, useEffect } from 'react';
import { PageContainer } from '@/components/common/PageContainer';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Receipt,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Inbox,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/hooks/useAuth';

interface OutstandingFeeShipment {
  shipment_id: string;
  tracking_number: string;
  delivery_fee: number;
  created_at: string;
  delivered_at?: string;
}

interface OutstandingFeeGroup {
  tenant_id: string;
  tenant_name: string;
  shipments: OutstandingFeeShipment[];
  total_owed: number;
  shipment_count: number;
}

/**
 * Outstanding tenant fees — delivered `sender_pays_all` shipments whose
 * delivery fee hasn't been settled yet. Carrier/admin can mark a single
 * shipment or a whole tenant batch paid (e.g. after a bank transfer arrives).
 * Tenants see this read-only as "fees I owe".
 */
export default function OutstandingFees() {
  const { tp } = useI18n();
  const { user } = useAuth();
  const s = tp.outstandingFees;
  const canMarkPaid = user?.role === 'carrier' || user?.role === 'admin';

  const [groups, setGroups] = useState<OutstandingFeeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState<Set<string>>(new Set());

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get<OutstandingFeeGroup[]>(
        '/api/v1/dashboard/outstanding-fees',
      );
      if (res.success) {
        setGroups(Array.isArray(res.data) ? res.data : []);
      } else {
        toast.error(res.message || s.loadError);
      }
    } catch {
      toast.error(s.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const markPaid = async (shipmentIds: string[]) => {
    if (!shipmentIds.length) return;
    setMarking(new Set(shipmentIds));
    try {
      const res = await api.post('/api/v1/dashboard/outstanding-fees/mark-paid', {
        shipment_ids: shipmentIds,
      });
      if (!res.success) {
        toast.error(res.message || s.markPaidError);
        return;
      }
      toast.success(s.markPaidSuccess);
      await refresh();
    } catch {
      toast.error(s.markPaidError);
    } finally {
      setMarking(new Set());
    }
  };

  const grandTotal = groups.reduce((sum, g) => sum + g.total_owed, 0);
  const totalShipments = groups.reduce((sum, g) => sum + g.shipment_count, 0);

  return (
    <PageContainer>
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-950">
            <Receipt className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{s.pageTitle}</h1>
            <p className="text-sm text-muted-foreground">
              {canMarkPaid ? s.pageSubtitleCarrier : s.pageSubtitleTenant}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={refresh}
          disabled={loading}
          title={s.refresh}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Summary banner */}
      {!loading && groups.length > 0 && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">{s.totalOwed}</p>
            <p className="text-2xl font-bold text-amber-600" dir="ltr">
              {formatCurrency(grandTotal)}
            </p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">{s.shipmentsCount}</p>
            <p className="text-2xl font-bold">{totalShipments}</p>
          </div>
          {canMarkPaid && (
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs text-muted-foreground">{s.tenantsCount}</p>
              <p className="text-2xl font-bold">{groups.length}</p>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <Inbox className="h-7 w-7 text-muted-foreground" />
            </div>
            <h3 className="mb-1 text-lg font-semibold">{s.emptyTitle}</h3>
            <p className="max-w-sm text-sm text-muted-foreground">
              {s.emptyDescription}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const allIds = group.shipments.map((x) => x.shipment_id);
            const batchMarking = allIds.every((id) => marking.has(id));
            return (
              <Card key={group.tenant_id}>
                <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold">
                      {group.tenant_name}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {group.shipment_count} {s.shipmentsLabel} · {' '}
                      <span className="font-medium text-amber-600" dir="ltr">
                        {formatCurrency(group.total_owed)}
                      </span>
                    </p>
                  </div>
                  {canMarkPaid && (
                    <Button
                      size="sm"
                      onClick={() => markPaid(allIds)}
                      disabled={marking.size > 0}
                      className="gap-2"
                    >
                      {batchMarking ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                      {s.markAllPaid}
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="divide-y">
                    {group.shipments.map((sh) => (
                      <div
                        key={sh.shipment_id}
                        className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium" dir="ltr">
                            {sh.tracking_number}
                          </p>
                          <p className="text-xs text-muted-foreground" dir="ltr">
                            {formatDate(new Date(sh.created_at), 'dd MMM yyyy')}
                          </p>
                        </div>
                        <span
                          className="text-sm font-semibold text-amber-600"
                          dir="ltr"
                        >
                          {formatCurrency(sh.delivery_fee)}
                        </span>
                        {canMarkPaid && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markPaid([sh.shipment_id])}
                            disabled={marking.size > 0}
                            className="gap-1"
                          >
                            {marking.has(sh.shipment_id) ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-3 w-3" />
                            )}
                            <span className="text-xs">{s.markPaid}</span>
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
